import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import HttpManager from "../../../services/HttpManager";
import { EquipoTablero } from "../../../services/v2/prospects.service";
import { PromoApplicationRow } from "../finances/promoApplications/interfaces";
import { Multi } from "../managment/enrollmentRequests/filtrosMatriculas";
import "../managment/enrollmentRequests/filtrosMatriculas.css";
import { errorText } from "../managment/enrollmentRequests/helpers";
import { logoDe } from "./tableroView";
import "./tableroLista.css";

/*
	Promociones del Tablero de Ventas (Esteban, 2026-11-04: "una lista total, filtrable, de un solo allí, que se pueda ver de dónde se
	derivaron los números"): todas las promociones aplicadas del mes en los planteles del alcance, una por renglón, con las cifras de lo
	filtrado arriba. Sale del mismo expediente de Promociones (GET _applications) plantel por plantel; el servidor ya decide qué ve cada
	quien (la asesora, lo de su equipo). Equipo = el rol de la asesora que la vendió.
*/
interface Props { campusId: number; month: string; scope: "campus" | "country"; planteles: number[]; equipos: EquipoTablero[]; }
interface Fila extends PromoApplicationRow { plantel: number; moneda: string; equipo: string; }

const PLANTEL: { [k: number]: string } = { 1: "MTY", 2: "TRC", 3: "TGU", 4: "SPS", 5: "Online", 7: "SAL" };
const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const APLICADA = 1;
const fecha = (s?: string | null) => { if (!s) return ""; const [y, m, d] = s.slice(0, 10).split("-").map(Number); return y ? `${d} ${MESES[m - 1]}` : ""; };
const dinero = (v: number, s: string) => (/^[A-Za-z]/.test(s) ? s + " " : s) + Number(v || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const entero = (n: number) => Number(n || 0).toLocaleString("es-MX");
/** Como GrupoVentas::deRol del servidor: "Asesora Fénix" → Fénix, "Asesora Admisiones Monterrey" → Admisiones MTY. */
const equipoDeRol = (rol: string | null | undefined, plantel: number): string => {
	const r = (rol || "").trim();
	if (!r) return "Sin asesora";
	if (/admisiones/i.test(r)) return /torre[oó]n/i.test(r) ? "Admisiones TRC" : /monterrey|m[eé]xico/i.test(r) || plantel === 1 ? "Admisiones MTY" : plantel === 2 ? "Admisiones TRC" : "Admisiones";
	if (/kits/i.test(r)) return "Kits";
	if (/^direcci[oó]n/i.test(r)) return "Dirección";
	return r.replace(/^(asesora|asesor|supervisi[oó]n( de ventas)?)\s+/i, "").replace(/\s*reingresos?\b/i, "").replace(/\s*-\s*entrenamiento$|\s+m[eé]xico$|\s+\d+$/i, "").trim() || r;
};
const ultimoDia = (mes: string) => { const [y, m] = mes.split("-").map(Number); return new Date(y, m, 0).getDate(); };

export default function TableroPromociones({ campusId, month, scope, planteles, equipos }: Props) {
	const [filas, setFilas] = useState<Fila[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [q, setQ] = useState("");
	const [fEquipos, setFEquipos] = useState<string[]>([]);
	const [fPromos, setFPromos] = useState<string[]>([]);
	const [fAsesoras, setFAsesoras] = useState<string[]>([]);
	const [fEstado, setFEstado] = useState<string[]>([]);
	const [fPlanteles, setFPlanteles] = useState<number[]>([]);

	useEffect(() => {
		let vigente = true;
		setFilas(null); setError(null);
		const http = HttpManager.getInstance();
		const from = month + "-01", to = month + "-" + String(ultimoDia(month)).padStart(2, "0");
		const lista = scope === "campus" ? [campusId] : planteles;
		(async () => {
			const out: Fila[] = [];
			let errores = 0, ultimo = "";
			for (const c of lista) {
				try {
					for (let page = 1; page < 20; page++) {
						const r = await http.promoApplicationsService.list(c, { from, to, perPage: 200, page });
						const d = r.data;
						(d.data || []).forEach((x: PromoApplicationRow) => out.push({ ...x, plantel: c, moneda: d.currencySymbol || "", equipo: equipoDeRol((x.advisors || [])[0]?.role, c) }));
						if ((d.data || []).length < 200 || out.length >= d.total) break;
					}
				} catch (e) { errores++; ultimo = errorText(e); }
			}
			if (!vigente) return;
			if (errores === lista.length) setError(ultimo || "No se pudieron leer las promociones."); else setFilas(out.sort((a, b) => (b.paymentDate || "").localeCompare(a.paymentDate || "") || b.id - a.id));
		})();
		return () => { vigente = false; };
	}, [campusId, month, scope, planteles]);

	const opEquipos = useMemo(() => Array.from(new Set((filas || []).map((f) => f.equipo))).sort().map((e) => ({ id: e, name: e })), [filas]);
	const opPromos = useMemo(() => Array.from(new Set((filas || []).map((f) => f.promoName))).sort().map((p) => ({ id: p, name: p })), [filas]);
	const opAsesoras = useMemo(() => {
		const m: { [k: string]: string } = {};
		(filas || []).forEach((f) => (f.advisors || []).forEach((a: any) => { if (a.name) m[a.name] = f.equipo; }));
		return Object.keys(m).sort().map((n) => ({ id: n, name: n, detalle: m[n] }));
	}, [filas]);
	const opPlanteles = useMemo(() => Array.from(new Set((filas || []).map((f) => f.plantel))).sort().map((p) => ({ id: p, name: PLANTEL[p] || String(p) })), [filas]);
	const opEstado = useMemo(() => Array.from(new Set((filas || []).map((f) => f.status || ""))).filter(Boolean).map((s) => ({ id: s, name: s })), [filas]);

	const filtradas = useMemo(() => {
		const t = q.trim().toLowerCase();
		return (filas || []).filter((f) => (!fEquipos.length || fEquipos.includes(f.equipo)) && (!fPromos.length || fPromos.includes(f.promoName))
			&& (!fAsesoras.length || (f.advisors || []).some((a: any) => fAsesoras.includes(a.name))) && (!fEstado.length || fEstado.includes(f.status || ""))
			&& (!fPlanteles.length || fPlanteles.includes(f.plantel))
			&& (!t || (f.student.name + " " + (f.student.identityCode || "") + " " + f.promoName + " " + (f.courseName || "") + " " + (f.advisors || []).map((a: any) => a.name).join(" ")).toLowerCase().includes(t)));
	}, [filas, q, fEquipos, fPromos, fAsesoras, fEstado, fPlanteles]);
	const hayFiltros = !!(q || fEquipos.length || fPromos.length || fAsesoras.length || fEstado.length || fPlanteles.length);
	const limpiar = () => { setQ(""); setFEquipos([]); setFPromos([]); setFAsesoras([]); setFEstado([]); setFPlanteles([]); };

	// Cifras de lo filtrado, por moneda: solo las Aplicadas suman (las anuladas se ven, no cuentan).
	const cifras = useMemo(() => {
		const m: { [s: string]: { aplicadas: number; cobrado: number; regalado: number; anuladas: number } } = {};
		filtradas.forEach((f) => {
			const c = (m[f.moneda] = m[f.moneda] || { aplicadas: 0, cobrado: 0, regalado: 0, anuladas: 0 });
			if (f.statusId === APLICADA) { c.aplicadas++; c.cobrado += f.amountPaid || 0; c.regalado += f.giftValue || 0; } else c.anuladas++;
		});
		return Object.entries(m);
	}, [filtradas]);

	if (error) return <div className="aviso error">{error}</div>;
	if (!filas) return <div className="ventas-vacio">Cargando…</div>;
	return (
		<div className="tl">
			<div className="tl-cab">
				<h4>Promociones Aplicadas</h4>
				<span className="sub">{filtradas.length} de {filas.length}</span>
				<div className="tl-busca">
					<FontAwesomeIcon icon={solid("magnifying-glass")} />
					<input type="search" value={q} placeholder="Alumno, promoción, curso o asesora" aria-label="Buscar" onChange={(e) => setQ(e.target.value)} />
				</div>
			</div>
			<div className="waFmBarra">
				<div className="waFmBarraListas">
					{opEquipos.length > 1 ? <Multi titulo="Equipo" opciones={opEquipos} elegidos={fEquipos} onChange={setFEquipos} /> : null}
					<Multi titulo="Promoción" buscar opciones={opPromos} elegidos={fPromos} onChange={setFPromos} />
					{opAsesoras.length > 1 ? <Multi titulo="Asesora" buscar opciones={opAsesoras} elegidos={fAsesoras} onChange={setFAsesoras} /> : null}
					{opPlanteles.length > 1 ? <Multi titulo="Plantel" opciones={opPlanteles} elegidos={fPlanteles} onChange={setFPlanteles} /> : null}
					{opEstado.length > 1 ? <Multi titulo="Estado" opciones={opEstado} elegidos={fEstado} onChange={setFEstado} /> : null}
					{hayFiltros ? <button type="button" className="btn btn-link btn-sm waFmBarraLimpiar" onClick={limpiar}>Limpiar Filtros</button> : null}
				</div>
			</div>
			<div className="tl-kpis">
				{cifras.length === 0 ? <div className="tl-kpi"><small>Aplicadas</small><b>0</b></div> : cifras.map(([s, c]) => (
					<React.Fragment key={s}>
						<div className="tl-kpi"><small>Aplicadas{cifras.length > 1 ? " " + s : ""}</small><b>{entero(c.aplicadas)}</b>{c.anuladas ? <i>{c.anuladas} anuladas</i> : null}</div>
						<div className="tl-kpi"><small>Cobrado{cifras.length > 1 ? " " + s : ""}</small><b className="verde">{dinero(c.cobrado, s)}</b></div>
						<div className="tl-kpi"><small>Regalado{cifras.length > 1 ? " " + s : ""}</small><b className="oro">{dinero(c.regalado, s)}</b></div>
						<div className="tl-kpi"><small>Regalado por cada {dinero(1, s)} cobrado</small><b>{c.cobrado > 0 ? dinero(c.regalado / c.cobrado, s) : "—"}</b></div>
					</React.Fragment>
				))}
			</div>
			<div className="tl-tabla-marco waScrollX">
				<table className="tl-tabla">
					<thead><tr><th>Fecha</th><th>Alumno</th><th>Promoción</th><th>Asesora</th>{opPlanteles.length > 1 ? <th>Plantel</th> : null}<th className="tl-num">Cobrado</th><th className="tl-num">Regalado</th><th>Estado</th></tr></thead>
					<tbody>
						{filtradas.length === 0 ? <tr><td colSpan={8} className="tl-vacio">{hayFiltros ? "Sin promociones con esos filtros." : "Sin promociones aplicadas este mes."}</td></tr> : null}
						{filtradas.map((f) => {
							const e = equipos.find((x) => x.equipo === f.equipo);
							return (
								<tr key={f.plantel + "-" + f.id} className={f.statusId === APLICADA ? "" : "tl-tachada"}>
									<td className="tl-fecha">{fecha(f.paymentDate)}</td>
									<td><span className="tl-nom">{f.student.name}</span><span className="tl-sub">{f.courseName || ""}{f.giftStudent ? " · regalo a " + f.giftStudent.name : ""}</span></td>
									<td><span className="tl-nom">{f.promoName}</span>{f.kindName ? <span className="tl-sub">{f.kindName}{(f.gifts || []).length ? " · " + (f.gifts || []).map((g: any) => g.name).filter(Boolean).join(", ") : ""}</span> : null}</td>
									<td>
										{(f.advisors || []).length ? <span className="tl-nom">{(f.advisors || []).map((a: any) => a.name).join(" + ")}</span> : <span className="tl-chip oro">Sin asesora</span>}
										<span className="tl-sub"><span className="tl-equipo">{e ? <img src={logoDe(e)} alt="" /> : null}{f.equipo}</span></span>
									</td>
									{opPlanteles.length > 1 ? <td>{PLANTEL[f.plantel] || f.plantel}</td> : null}
									<td className="tl-num">{dinero(f.amountPaid || 0, f.moneda)}</td>
									<td className="tl-num">{f.giftValue ? dinero(f.giftValue, f.moneda) : <span className="tl-sub">—</span>}</td>
									<td><span className={"tl-chip " + (f.statusId === APLICADA ? "verde" : "rojo")}>{f.status || (f.statusId === APLICADA ? "Aplicada" : "Anulada")}</span></td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
}
